import { queryOptions, useQuery } from '@tanstack/react-query';
import { z } from 'zod';
import {
  staffConfigModeQueryKeyPart,
  staffConfigModeQueryParams,
} from '~/hooks/analyticsStatAccess/statAccessQueryScope';
import type { Genie2ReportingMode } from '~/utils/analyticsReportingMode';
import { customFetch } from '~/utils/customFetch';

import {
  staffGlobalStatDiscoveryEntrySchema,
  type StaffGlobalCatalogScope,
  type StaffGlobalStatDiscoveryEntry,
} from '../useStaffGlobalStatColumnCatalogQuery/staffGlobalStatColumnCatalogSchema';

const staffGlobalStatsResponseSchema = z.object({
  stats: z.array(staffGlobalStatDiscoveryEntrySchema),
});

export type StaffGlobalStatsResponse = z.infer<typeof staffGlobalStatsResponseSchema>;

export type { StaffGlobalStatDiscoveryEntry };

export type StaffGlobalStatsQueryOptions = {
  scope?: StaffGlobalCatalogScope;
  analyticsReportingMode?: Genie2ReportingMode | null;
};

const fetchStaffGlobalStats = async (
  options?: StaffGlobalStatsQueryOptions,
): Promise<StaffGlobalStatsResponse> => {
  const { scope, analyticsReportingMode } = options ?? {};
  const params = staffConfigModeQueryParams(analyticsReportingMode, scope ? { scope } : undefined);
  const { data } = await customFetch.get<unknown>('/authenticated/staff/analytics/stats', {
    headers: { 'Content-Type': 'application/json' },
    useNodeEndpoint: true,
    params,
  });
  return staffGlobalStatsResponseSchema.parse(data);
};

export const getStaffGlobalStatsQueryOptions = (
  scopeOrOptions?: StaffGlobalCatalogScope | StaffGlobalStatsQueryOptions,
) => {
  const options: StaffGlobalStatsQueryOptions =
    scopeOrOptions == null || typeof scopeOrOptions === 'string'
      ? { scope: scopeOrOptions }
      : scopeOrOptions;
  return queryOptions({
    queryKey: [
      'staff',
      'global-stats',
      options.scope ?? 'all',
      staffConfigModeQueryKeyPart(options.analyticsReportingMode),
    ],
    queryFn: () => fetchStaffGlobalStats(options),
    staleTime: 60 * 1000,
    refetchOnWindowFocus: false,
  });
};

export const useStaffGlobalStatsQuery = (
  scopeOrOptions?: StaffGlobalCatalogScope | StaffGlobalStatsQueryOptions,
) => useQuery(getStaffGlobalStatsQueryOptions(scopeOrOptions));
